import { useCallback } from "react";
import axios from "axios";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { protectedResources } from "../../authConfig";

const API_BASE = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/packaging-check`;

export const getApiErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const triggerDownload = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// Error responses of file downloads arrive as a Blob holding JSON
const readBlobError = async (error) => {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      return new Error(JSON.parse(await data.text()).message);
    } catch {
      return error;
    }
  }
  return error;
};

const usePackagingCheckApi = () => {
  const { instance, accounts } = useMsal();

  const getAccessToken = useCallback(async () => {
    const request = { scopes: protectedResources.apiScopes.scopes.read, account: accounts[0] };
    try {
      const result = await instance.acquireTokenSilent(request);
      return result.accessToken;
    } catch (silentError) {
      if (
        silentError instanceof InteractionRequiredAuthError ||
        silentError?.errorCode === "monitor_window_timeout"
      ) {
        await instance.acquireTokenRedirect(request);
        return null;
      }
      throw silentError;
    }
  }, [accounts, instance]);

  const authHeaders = useCallback(async () => {
    const token = await getAccessToken();
    return { Authorization: `Bearer ${token}` };
  }, [getAccessToken]);

  const validateFile = useCallback(
    async (file, { signal } = {}) => {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await axios.post(`${API_BASE}/validate`, formData, {
        headers: await authHeaders(),
        signal,
      });
      return data;
    },
    [authHeaders],
  );

  const createCheck = useCallback(
    async ({ previousFile, updatedFile, title, product, corrections }, onUploadProgress) => {
      const formData = new FormData();
      formData.append("previousFile", previousFile);
      formData.append("updatedFile", updatedFile);
      formData.append("title", title || "");
      formData.append("product", JSON.stringify(product));
      formData.append("corrections", JSON.stringify(corrections));
      const { data } = await axios.post(API_BASE, formData, {
        headers: await authHeaders(),
        onUploadProgress,
      });
      return data;
    },
    [authHeaders],
  );

  const listChecks = useCallback(async () => {
    const { data } = await axios.get(API_BASE, { headers: await authHeaders() });
    return data;
  }, [authHeaders]);

  const getCheck = useCallback(
    async (id) => {
      const { data } = await axios.get(`${API_BASE}/${id}`, { headers: await authHeaders() });
      return data;
    },
    [authHeaders],
  );

  const getChecklist = useCallback(async () => {
    const { data } = await axios.get(`${API_BASE}/settings/checklist`, { headers: await authHeaders() });
    return data;
  }, [authHeaders]);

  const saveChecklist = useCallback(
    async (items) => {
      const { data } = await axios.put(`${API_BASE}/settings/checklist`, { items }, { headers: await authHeaders() });
      return data;
    },
    [authHeaders],
  );

  const rerunCorrectionsControl = useCallback(
    async (id) => {
      const { data } = await axios.post(`${API_BASE}/${id}/corrections-control`, null, { headers: await authHeaders() });
      return data;
    },
    [authHeaders],
  );

  // The archived Excel report (spec 3.6); fileName is used when the
  // Content-Disposition header is not exposed (cross-origin in development)
  const downloadReport = useCallback(
    async (id, fileName) => {
      try {
        const response = await axios.get(`${API_BASE}/${id}/report.xlsx`, {
          headers: await authHeaders(),
          responseType: "blob",
        });
        const encoded = (response.headers["content-disposition"] || "").match(/filename\*=UTF-8''([^;]+)/);
        triggerDownload(response.data, encoded ? decodeURIComponent(encoded[1]) : fileName);
      } catch (error) {
        throw await readBlobError(error);
      }
    },
    [authHeaders],
  );

  return {
    validateFile,
    createCheck,
    listChecks,
    getCheck,
    getChecklist,
    saveChecklist,
    rerunCorrectionsControl,
    downloadReport,
  };
};

export default usePackagingCheckApi;
